import { FILTER_URL_KEYS, FilterUrlKey } from '../types';

const KEYWORD_PARAM = 'q';

type LocationSearch = {
    search: (key: string, value?: string | null) => void;
};

/**
 * Decode the browse keyword URL param (`?q=`).
 * Mirrors `items-search-query-controller.js#checkRouteParams`:
 * `routeParams.q.split("+").join(" ")`.
 */
export function decodeKeywordParam(raw: string | null | undefined): string {
    if (!raw) {
        return '';
    }
    return raw.split('+').join(' ');
}

/**
 * Encode a browse keyword for the URL.
 * Mirrors Angular `urlParam` directive: spaces become `+`.
 */
export function encodeKeywordParam(keyword: string): string {
    if (!keyword) {
        return '';
    }
    return keyword.split(' ').join('+');
}

export function readKeywordFromUrl(search: string = window.location.search): string {
    const match = search.match(/[?&]q=([^&]*)/);
    if (!match) {
        return '';
    }
    return decodeKeywordParam(decodeURIComponent(match[1]));
}

type AngularInjector = {
    get: (name: string) => unknown;
};

function getAngularInjector(): AngularInjector | null {
    try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const angularGlobal = (window as any).angular;
        return angularGlobal?.element(document.body)?.injector?.() ?? null;
    } catch {
        // Storybook / unit tests.
        return null;
    }
}

function getAngularLocation(): LocationSearch | null {
    const injector = getAngularInjector();
    if (!injector) {
        return null;
    }
    return injector.get('$location') as LocationSearch;
}

/**
 * `$location.search` from React does not run inside an Angular digest, so
 * `$routeUpdate` / `itemSearchQueryChanged` / `NewFetchItems` never fire and
 * the items list stays unfiltered. Queue a digest like `itemsList.tsx` does
 * after mutating `$rootScope`.
 */
function getAngularRootScope(): {
    $evalAsync?: (fn?: () => void) => void;
    $broadcast?: (name: string, ...args: unknown[]) => void;
} | null {
    try {
        const injector = getAngularInjector();
        return (injector?.get('$rootScope') as {
            $evalAsync?: (fn?: () => void) => void;
            $broadcast?: (name: string, ...args: unknown[]) => void;
        } | undefined) ?? null;
    } catch {
        return null;
    }
}

function scheduleAngularDigest(): void {
    getAngularRootScope()?.$evalAsync?.();
}

/**
 * Mirror Angular `SearchQueryController#removeFilter` for keyword chips:
 * `$rootScope.$broadcast('itemSearchQueryChanged', {keyword: ''})`.
 * Without this, `$routeUpdate` → `checkRouteParams` restores `?q=` from the
 * still-populated `$scope.query.keyword` and the items list never refreshes.
 */
export function notifyAngularItemSearchQueryChanged(
    query: Record<string, unknown>,
): void {
    const $rootScope = getAngularRootScope();
    if (!$rootScope?.$broadcast) {
        return;
    }

    const payload = {
        ...query,
        keyword: typeof query.keyword === 'string' ? query.keyword : '',
    };
    const emit = () => $rootScope.$broadcast?.('itemSearchQueryChanged', payload);

    if ($rootScope.$evalAsync) {
        $rootScope.$evalAsync(emit);
        return;
    }

    emit();
}

/**
 * Angular `urlParam` patches `encodeURIComponent` so `$location.search` keeps
 * `+` (spaces) instead of writing `%2B`. See directives.js and
 * https://github.com/angular/angular.js/issues/3042
 */
function withPlusPreservedEncode(run: () => void): void {
    const realEncodeURIComponent = window.encodeURIComponent;
    window.encodeURIComponent = ((input: string) =>
        realEncodeURIComponent(input).split('%2B').join('+')) as typeof encodeURIComponent;
    try {
        run();
    } finally {
        window.encodeURIComponent = realEncodeURIComponent;
    }
}

function buildSearchWithKeyword(existingSearch: string, keyword: string): string {
    const params = new URLSearchParams(existingSearch);
    params.delete('q');

    const rest = params.toString();
    const encoded = encodeKeywordParam(keyword);

    if (!encoded) {
        return rest ? `?${rest}` : '';
    }

    const qPart = `q=${encoded}`;
    return rest ? `?${rest}&${qPart}` : `?${qPart}`;
}

/**
 * Write the browse keyword to the URL without a full navigation.
 * Uses Angular `$location` when available so `$routeUpdate` keeps working;
 * falls back to `history.replaceState` (Storybook / unit tests).
 */
export function writeKeywordToUrl(keyword: string): void {
    const encoded = encodeKeywordParam(keyword);
    const $location = getAngularLocation();

    if ($location) {
        withPlusPreservedEncode(() => {
            $location.search(KEYWORD_PARAM, encoded || null);
        });
        scheduleAngularDigest();
        return;
    }

    const url = new URL(window.location.href);
    url.search = buildSearchWithKeyword(url.search, keyword);
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
}

export function readFilterParamsFromUrl(
    search: string = window.location.search,
): Partial<Record<FilterUrlKey, string>> {
    const params = new URLSearchParams(search);
    const result: Partial<Record<FilterUrlKey, string>> = {};

    for (const key of FILTER_URL_KEYS) {
        const value = params.get(key);
        if (value) {
            result[key] = value;
        }
    }

    return result;
}

/**
 * Write browse filter query params (not `q`) without a full navigation.
 * Preserves the existing `?q=` encoding.
 */
export function writeFilterParamsToUrl(
    updates: Partial<Record<FilterUrlKey, string | null>>,
): void {
    const $location = getAngularLocation();

    if ($location) {
        for (const [key, value] of Object.entries(updates)) {
            $location.search(key, value || null);
        }
        scheduleAngularDigest();
        return;
    }

    const keyword = readKeywordFromUrl();
    const params = new URLSearchParams(window.location.search);
    params.delete(KEYWORD_PARAM);

    for (const [key, value] of Object.entries(updates)) {
        if (value == null || value === '') {
            params.delete(key);
        } else {
            params.set(key, value);
        }
    }

    const rest = params.toString();
    const search = buildSearchWithKeyword(rest ? `?${rest}` : '', keyword);
    const url = new URL(window.location.href);
    url.search = search;
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
}
